import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useCareZones from './useCareZones';
import { CheckInStatuses } from 'src/constants/CheckIn';
import usePageVisibility from 'src/hooks/usePageVisibility';
import { selectFacility } from 'src/store/configSlice';
import { GetCheckInsApiArg, useGetCheckInsQuery } from 'src/store/sageAdminApi';
import { numberAwareSort } from 'src/utils/listUtils';

export const DateFormat = 'yyyy-MM-dd';

interface UseCheckInsProps {
    date?: string;
    includeOptedOutResidents?: boolean;
    isOnHold?: boolean;
    pollingInterval?: number;
    status?: string;
    unitId?: string;
}

function useCheckIns(props: UseCheckInsProps = {}) {
    const {
        date = format(new Date(), DateFormat),
        includeOptedOutResidents = false,
        isOnHold = false,
        pollingInterval = 0,
        status,
        unitId,
    } = props;

    const browserTabIsVisible = usePageVisibility();

    const facility = useSelector(selectFacility);

    const { isCheckInsEnabled, isLoading: isLoadingCareZones } = useCareZones();

    const isDuringDowntime = useMemo(() => {
        // If a timezone is configured on the facility, check to see if the facility
        // time is between 12am and 4am. Otherwise, check the local time
        let currentHour;
        if (facility?.timezone) {
            currentHour = formatInTimeZone(new Date(), facility.timezone, 'H');
        } else {
            currentHour = format(new Date(), 'H');
        }

        // Currently, this is localized even though we already generate check-ins at 4 am ET
        return parseInt(currentHour) < 4;
    }, [facility]);

    const getCheckInsRequest = useMemo(() => {
        const baseRequestBody: GetCheckInsApiArg = {
            date,
            facilityId: facility.id,
            ...(unitId && { unitId }),
        };

        if (isOnHold) {
            return {
                ...baseRequestBody,
                isOnHold: true,
                status: CheckInStatuses.SkippedOnHold,
            };
        } else {
            return {
                ...baseRequestBody,
                isOnHold: false,
                status,
            };
        }
    }, [date, facility.id, isOnHold, status, unitId]);

    const shouldSkip = isDuringDowntime || !facility || !isCheckInsEnabled;

    const { checkIns, currentData, isFetching, isLoading, isUninitialized, refetch } =
        useGetCheckInsQuery(getCheckInsRequest, {
            refetchOnFocus: true,
            pollingInterval: browserTabIsVisible ? pollingInterval : 0,
            skip: shouldSkip,
            selectFromResult: (results) => ({
                ...results,
                checkIns: numberAwareSort(results?.data || [], 'unit.displayName'),
            }),
        });

    const filteredCheckIns = useMemo(() => {
        if (includeOptedOutResidents) {
            return checkIns;
        }

        return checkIns?.filter((checkIn) =>
            checkIn?.unit?.currentOccupants?.every(
                (resident) => resident.isCheckInsEnabled !== false
            )
        );
    }, [checkIns, includeOptedOutResidents]);

    return {
        checkIns: filteredCheckIns,
        // `currentData` is undefined only until the current args resolve, and survives a poll
        hasCurrentData: currentData !== undefined,
        isFetching,
        isLoading,
        isDuringDowntime,
        // Skipped for good: care zones have loaded and check-ins don't apply (or it's downtime)
        isSkipped: shouldSkip && !isLoadingCareZones,
        isUninitialized,
        numResults: filteredCheckIns?.length,
        refetch,
    };
}

export default useCheckIns;
